import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ApprovalStatusBadge } from '@/components/approvals/ApprovalStatusBadge';
import { ApprovalDiffTable } from '@/components/approvals/ApprovalDiffTable';
import { ApprovalStageTimeline } from '@/components/approvals/ApprovalStageTimeline';
import {
  useApprovalRequest,
  useApprovalDecision,
  parseDiff,
} from '@/api/hooks/useApprovalRequests';
import { ArrowLeft, Check, X, Undo2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

type PendingAction = 'approve' | 'reject' | 'withdraw' | null;

export default function ApprovalRequestDetailPage() {
  const { t } = useTranslation('approvals');
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const requestId = Number(id);

  const { data: request, isLoading, isError } = useApprovalRequest(requestId);
  const [action, setAction] = useState<PendingAction>(null);
  const [comment, setComment] = useState('');

  const approve = useApprovalDecision('approve');
  const reject = useApprovalDecision('reject');
  const withdraw = useApprovalDecision('withdraw');

  const mutation = action === 'approve' ? approve : action === 'reject' ? reject : withdraw;

  const submit = async () => {
    if (!action) return;
    try {
      await mutation.mutateAsync({ id: requestId, comment: comment.trim() || undefined });
      toast.success(t(`detail.toast.${action}`));
      setAction(null);
      setComment('');
    } catch (e: any) {
      // The server returns a typed code for each refusal (SelfApprovalBlocked,
      // NotAnApprover, AlreadyDecided...). Surface its message rather than a
      // generic failure, because each one has a different fix.
      toast.error(e?.response?.data?.message ?? t('detail.toast.error'));
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (isError || !request) {
    return <p className="text-sm text-destructive">{t('detail.loadError')}</p>;
  }

  const diffs = parseDiff(request.diffJson);
  // Rejection must say why; approval need not.
  const commentRequired = action === 'reject';
  const canSubmit = !commentRequired || comment.trim().length > 0;

  return (
    <div className="space-y-4">
      <Button variant="ghost" size="sm" onClick={() => navigate('/approvals')} className="gap-1">
        <ArrowLeft className="h-4 w-4" />
        {t('detail.back')}
      </Button>

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">
            {request.targetLabel ?? request.targetId}
          </h1>
          <p className="text-sm text-muted-foreground">
            <span className="font-mono">{request.operationKind}</span>
            {' · '}
            {t('detail.policy')}: {request.policyCode} v{request.policyVersion}
          </p>
        </div>
        <ApprovalStatusBadge status={request.status} />
      </div>

      {/* An approved change that did NOT land is the state most likely to be
          missed, so it gets a banner rather than a status pill alone. */}
      {(request.status === 'CONFLICTED' || request.status === 'APPLY_FAILED') && request.applyError && (
        <div className="flex gap-2 rounded-md border border-destructive/50 bg-destructive/10 p-3">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
          <div className="text-sm">
            <p className="font-medium text-destructive">{t(`detail.banner.${request.status}`)}</p>
            <p className="mt-1 text-muted-foreground">{request.applyError}</p>
          </div>
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle>{t('detail.proposedChange')}</CardTitle>
          <CardDescription>{t('detail.proposedChangeHint')}</CardDescription>
        </CardHeader>
        <CardContent>
          <ApprovalDiffTable diffs={diffs} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('detail.signOffChain')}</CardTitle>
        </CardHeader>
        <CardContent>
          <ApprovalStageTimeline stages={request.stages} />
        </CardContent>
      </Card>

      {request.decisions.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>{t('detail.activity')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {request.decisions.map((d) => (
              <div key={d.approvalDecisionId} className="border-l-2 pl-3 text-sm">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium">
                    {t(`decision.${d.decision}`, { defaultValue: d.decision })}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {d.decidedByUserName?.trim()
                      ? d.decidedByUserName.trim()
                      : t('detail.byUser', { userId: d.decidedByUserId })}
                    {' · '}
                    {new Date(d.decidedDate).toLocaleString()}
                  </span>
                </div>
                {d.comment && <p className="mt-1 text-muted-foreground">{d.comment}</p>}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {(request.canDecide || request.canWithdraw) && (
        <div className="flex flex-wrap gap-2">
          {request.canDecide && (
            <>
              <Button onClick={() => setAction('approve')} className="gap-1">
                <Check className="h-4 w-4" />
                {t('detail.approve')}
              </Button>
              <Button variant="destructive" onClick={() => setAction('reject')} className="gap-1">
                <X className="h-4 w-4" />
                {t('detail.reject')}
              </Button>
            </>
          )}
          {request.canWithdraw && (
            <Button variant="outline" onClick={() => setAction('withdraw')} className="gap-1">
              <Undo2 className="h-4 w-4" />
              {t('detail.withdraw')}
            </Button>
          )}
        </div>
      )}

      <Dialog open={action !== null} onOpenChange={(open) => !open && setAction(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{action && t(`detail.dialog.${action}.title`)}</DialogTitle>
            <DialogDescription>
              {action && t(`detail.dialog.${action}.description`)}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2">
            <Label htmlFor="approval-comment">
              {commentRequired ? t('detail.commentRequired') : t('detail.commentOptional')}
            </Label>
            <Textarea
              id="approval-comment"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={4}
            />
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setAction(null)}>
              {t('detail.cancel')}
            </Button>
            <Button
              onClick={submit}
              disabled={!canSubmit || mutation.isPending}
              variant={action === 'reject' ? 'destructive' : 'default'}
            >
              {t('detail.confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
